import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { LoginContent } from '@/content/LoginContent';
import { Banner } from '../Banner';
import { QuestionIcon } from '@phosphor-icons/react';
import { Input } from '../ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { REGEXP_ONLY_DIGITS } from 'input-otp';

export type LoginTab = 'email-link' | 'password';

export interface LoginFormProps {
  email: string;
  password: string;
  otpCode: string;
  error: string | null;
  loading: boolean;
  codeSent: boolean;
  activeTab: LoginTab;
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onOtpCodeChange: (code: string) => void;
  onTabChange: (tab: LoginTab) => void;
  onPasswordSubmit: (e: React.FormEvent) => void;
  onOtpRequest: (e: React.FormEvent) => void;
  onOtpVerify: (e: React.FormEvent) => void;
  onResetCode: () => void;
}

/**
 * Presentational LoginForm component
 * Handles all UI rendering and delegates all state management and business logic to parent
 */
export function LoginForm({
  email,
  password,
  otpCode,
  error,
  loading,
  codeSent,
  activeTab,
  onEmailChange,
  onPasswordChange,
  onOtpCodeChange,
  onTabChange,
  onPasswordSubmit,
  onOtpRequest,
  onOtpVerify,
  onResetCode,
}: LoginFormProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <div className="relative flex justify-center min-h-screen px-4 pt-12 pb-8 sm:pt-24 sm:items-center overflow-hidden z-0">
      {/* Animated gradient banner at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[30vh] overflow-hidden pointer-events-none">
        <img
          src="/assets/images/banner-red.svg"
          alt=""
          className="animate-marquee-sway h-full w-auto min-w-[200vw] object-cover object-left"
          aria-hidden="true"
        />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="flex flex-col justify-center items-center">
            <img
              className="mb-3"
              src="/assets/logos/logo-icon.svg"
              alt={LoginContent.logoAlt}
              width={60}
            />
            <img
              src="/assets/logos/logo-font.svg"
              alt={LoginContent.logoAlt}
              width={60}
            />
          </div>

          <div className="h-[1px] bg-slate-600 w-full mt-4 mb-8" />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => onTabChange(value as LoginTab)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email-link">
              {LoginContent.tabs.emailLink}
            </TabsTrigger>
            <TabsTrigger value="password">
              {LoginContent.tabs.password}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email-link" className="mt-6">
            {codeSent ? (
              <div className="space-y-6">
                <Banner
                  variant="success"
                  description={LoginContent.magicLink.success.message}
                />
                <form
                  onSubmit={onOtpVerify}
                  className="space-y-6 flex flex-col gap-2 items-center"
                >
                  <div>
                    <label
                      htmlFor="otp-code-input"
                      className="block text-sm font-medium mb-2 text-foreground"
                    >
                      {LoginContent.magicLink.codeInput.label}
                    </label>
                    <InputOTP
                      maxLength={6}
                      inputMode="numeric"
                      pattern={REGEXP_ONLY_DIGITS}
                      value={otpCode}
                      onChange={(value) => onOtpCodeChange(value)}
                      disabled={loading}
                      id="otp-code-input"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  {error && <Banner variant="error" description={error} />}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading || otpCode.length !== 6}
                    className="w-full bg-sky-400"
                  >
                    {loading
                      ? LoginContent.magicLink.codeInput.verifying
                      : LoginContent.magicLink.codeInput.verify}
                  </Button>
                </form>
                <div className="flex flex-col gap-3">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full"
                    onClick={onResetCode}
                    disabled={loading}
                  >
                    {LoginContent.magicLink.success.sendAnother}
                  </Button>
                  <Button
                    size="lg"
                    variant="link"
                    className="w-full"
                    onClick={() => setIsDrawerOpen(true)}
                    disabled={loading}
                  >
                    <QuestionIcon size={24} />
                    {LoginContent.magicLink.notGettingEmail.button}
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={onOtpRequest} className="space-y-6">
                <div>
                  <label
                    htmlFor="email-link-input"
                    className="block text-sm font-medium mb-2 text-foreground"
                  >
                    {LoginContent.form.email.label}
                  </label>
                  <Input
                    id="email-link-input"
                    type="email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder={LoginContent.form.email.placeholder}
                    disabled={loading}
                  />
                </div>

                {error && <Banner variant="error" description={error} />}

                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full bg-sky-400"
                >
                  {loading
                    ? LoginContent.magicLink.submit.sending
                    : LoginContent.magicLink.submit.label}
                </Button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="password" className="mt-6">
            <form
              onSubmit={onPasswordSubmit}
              className="space-y-6 bg-slate-700 z-10 relative pb-6 rounded-lg"
            >
              <div>
                <label
                  htmlFor="email-password-input"
                  className="block text-sm font-medium mb-2 text-foreground"
                >
                  {LoginContent.form.email.label}
                </label>
                <Input
                  id="email-password-input"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder={LoginContent.form.email.placeholder}
                  disabled={loading}
                />
              </div>

              <div>
                <label
                  htmlFor="password-input"
                  className="block text-sm font-medium mb-2 text-foreground"
                >
                  {LoginContent.form.password.label}
                </label>

                <Input
                  id="password-input"
                  type="password"
                  value={password}
                  onChange={(e) => onPasswordChange(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder={LoginContent.form.password.placeholder}
                  disabled={loading}
                />
              </div>

              {error && <Banner variant="error" description={error} />}

              <Button
                size="lg"
                type="submit"
                disabled={loading}
                className="w-full bg-sky-400"
              >
                {loading
                  ? LoginContent.password.submit.signingIn
                  : LoginContent.password.submit.label}
              </Button>

              <p className="text-body text-slate-500 w-5/6 mx-auto text-center">
                {LoginContent.password.noAccount.message}{' '}
                <button
                  type="button"
                  onClick={() => onTabChange('email-link')}
                  className="text-sky-400 underline hover:text-sky-300 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 rounded"
                >
                  {LoginContent.password.noAccount.linkText}
                </button>
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>
              {LoginContent.magicLink.notGettingEmail.drawer.title}
            </DrawerTitle>
            <DrawerDescription>
              {LoginContent.magicLink.notGettingEmail.drawer.description}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-6 pb-6">
            <ul className="space-y-3 text-body text-slate-300">
              {LoginContent.magicLink.notGettingEmail.drawer.tips.map(
                (tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-sky-400 mt-0.5">•</span>
                    <span>{tip}</span>
                  </li>
                )
              )}
            </ul>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

